import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Loader2, CalendarDays, RefreshCw, CheckCircle2, Phone, Sparkles, ClipboardCheck, X, ChevronRight, ChevronLeft, ChevronDown, Pencil, BookOpen } from 'lucide-react';
import FlashSeanceModal from '../components/FlashSeanceModal';
import FinDeCoursModal from '../components/FinDeCoursModal';

const TYPE_LABEL = { conduite: 'Conduite', code: 'Code', examen: 'Examen', rvp: 'RVP', moto_plateau: 'Moto · Plateau' };
const TypeTag = ({ type }) => (type === 'moto_plateau'
  ? <span className="text-violet-300 font-bold">🏍 Moto · Plateau</span>
  : <>{TYPE_LABEL[type] || type}</>);
const STATUS_BADGE = {
  effectue: ['Effectuée ✓', 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'],
  annule: ['Annulée', 'bg-red-500/15 border-red-500/40 text-red-300'],
  absent_excuse: ['Abs. excusée', 'bg-orange-500/15 border-orange-500/40 text-orange-300'],
  absent_non_excuse: ['Abs. non excusée', 'bg-red-500/15 border-red-500/40 text-red-300'],
};

const isoDay = (d) => d.toISOString().slice(0, 10);
const VIEWS = [
  { id: 'jour', label: 'Jour' },
  { id: 'semaine', label: 'Semaine' },
  { id: 'mois', label: 'Mois' },
  { id: 'tout', label: 'Tout' },
];

const normName = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().split(/\s+/).sort().join(' ');

function periodRange(view, offset) {
  const now = new Date();
  if (view === 'jour') {
    const d = new Date(now); d.setDate(d.getDate() + offset);
    return [isoDay(d), isoDay(d)];
  }
  if (view === 'semaine') {
    const d = new Date(now);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
    const end = new Date(d); end.setDate(end.getDate() + 6);
    return [isoDay(d), isoDay(end)];
  }
  if (view === 'mois') {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1, 12);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0, 12);
    return [isoDay(d), isoDay(end)];
  }
  return [null, null];
}

function periodLabel(view, start, end) {
  if (view === 'jour') return new Date(start + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  if (view === 'semaine') return `Semaine du ${new Date(start + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${new Date(end + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
  if (view === 'mois') return new Date(start + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return '';
}

export default function MyPlanningPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [linked, setLinked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [actionLesson, setActionLesson] = useState(null);
  const [flashFor, setFlashFor] = useState(null);
  const [finFor, setFinFor] = useState(null);
  const [view, setView] = useState(() => { try { return localStorage.getItem('cv_planning_view') || 'semaine'; } catch (_) { return 'semaine'; } });
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [flashHistory, setFlashHistory] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [fixSaving, setFixSaving] = useState(false);
  const [fixMsg, setFixMsg] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addMsg, setAddMsg] = useState('');
  const [addForm, setAddForm] = useState({ date: new Date().toISOString().slice(0, 10), start_time: '09:00', duration_min: 60, type: 'conduite', student_name: '', notes: '' });
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ date: '', start_time: '' });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actMsg, setActMsg] = useState('');
  const [actSaving, setActSaving] = useState(false);
  const loadRef = useRef(() => {});

  const doReschedule = async () => {
    if (actSaving || !actionLesson) return;
    setActSaving(true); setActMsg('');
    try {
      const r = await api.rescheduleMyLesson(actionLesson.id, moveForm);
      await load();
      setActMsg(`Leçon reportée au ${new Date(r.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à ${r.start_time} ✓`);
      setActionLesson((prev) => (prev ? { ...prev, date: r.date, start_time: r.start_time, status: 'planifie' } : prev));
      setMoveOpen(false);
    } catch (e) {
      setActMsg(e?.response?.data?.detail || 'Erreur — réessaie.');
    }
    setActSaving(false);
  };

  const doCancel = async (status) => {
    if (actSaving || !actionLesson) return;
    setActSaving(true); setActMsg('');
    try {
      await api.cancelMyLesson(actionLesson.id, status);
      await load();
      setActMsg(status === 'annule' ? 'Leçon annulée ✓' : status === 'absent_excuse' ? 'Absence excusée enregistrée ✓' : 'Absence non excusée enregistrée ✓');
      setActionLesson((prev) => (prev ? { ...prev, status } : prev));
      setCancelOpen(false);
    } catch (e) {
      setActMsg(e?.response?.data?.detail || 'Erreur — réessaie.');
    }
    setActSaving(false);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (addSaving) return;
    if ((addForm.student_name || '').trim().length < 2) { setAddMsg('Indique le nom de l\u2019élève.'); return; }
    setAddSaving(true); setAddMsg('');
    try {
      const r = await api.addMyLesson({ ...addForm, duration_min: Number(addForm.duration_min), student_name: addForm.student_name.trim() });
      await load();
      setAddMsg(r.created_fiche ? `Leçon ajoutée ✓ — nouvelle fiche « ${r.student_name} » créée dans la base élèves.` : `Leçon ajoutée pour ${r.student_name} ✓`);
      setAddForm((p) => ({ ...p, student_name: '', notes: '' }));
    } catch (err) {
      setAddMsg(err?.response?.data?.detail || 'Erreur — réessaie.');
    }
    setAddSaving(false);
  };

  const fixStudent = async (mode) => {
    const name = (editingName || '').trim();
    if (name.length < 2 || !actionLesson) return;
    setFixSaving(true); setFixMsg('');
    try {
      const r = await api.fixLessonStudent(actionLesson.id, { name, mode });
      await load();
      setActionLesson((prev) => (prev ? { ...prev, student_name: r.student_name } : prev));
      setFixMsg(mode === 'rename'
        ? 'Orthographe corrigée ✓ — fiche élève mise à jour partout.'
        : r.created_fiche ? `Leçon réaffectée ✓ — nouvelle fiche « ${r.student_name} » créée dans la base élèves.` : `Leçon réaffectée à ${r.student_name} ✓`);
      setEditingName(null);
    } catch (e) {
      setFixMsg(e?.response?.data?.detail || 'Erreur — réessaie.');
    }
    setFixSaving(false);
  };

  useEffect(() => {
    if (!actionLesson) { setFlashHistory(null); return; }
    api.flashSeanceList().then((items) => setFlashHistory(items || [])).catch(() => setFlashHistory([]));
  }, [actionLesson]);

  const changeView = (v) => {
    setView(v); setOffset(0); setExpanded({});
    try { localStorage.setItem('cv_planning_view', v); } catch (_) { /* silent */ }
  };
  const moveOffset = (delta) => { setOffset((o) => o + delta); setExpanded({}); };

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const d = await api.myPlanning();
      setItems(d.items || []); setLinked(d.linked);
    } catch (_) { /* noop */ }
    setLoading(false); setRefreshing(false);
  }, []);
  loadRef.current = load;

  useEffect(() => {
    if (authLoading || !user) return;
    load();
    const iv = setInterval(() => loadRef.current(), 30000);
    const onFocus = () => loadRef.current();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [authLoading, user, load]);

  const isTeacher = user?.role === 'enseignant';

  const completeLesson = async (l) => {
    setCompleting(l.id);
    try {
      await api.completeMyLesson(l.id);
      setItems((prev) => prev.map((x) => (x.id === l.id ? { ...x, status: 'effectue' } : x)));
      navigate(`/enseignant/examen-blanc-grille?eleve=${encodeURIComponent(l.student_name)}`);
    } catch (_) { window.alert('Impossible de marquer la leçon — réessaie.'); }
    setCompleting(null);
  };

  if (authLoading) return <PageShell><div className="max-w-4xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user) return <PageShell><div className="max-w-3xl mx-auto p-12 text-center text-white/60">Connecte-toi pour voir ton planning.</div></PageShell>;

  const todayIso = new Date().toISOString().slice(0, 10);
  const [pStart, pEnd] = periodRange(view, offset);
  const filtered = pStart ? items.filter((l) => l.date >= pStart && l.date <= pEnd) : items;
  const byDate = filtered.reduce((acc, l) => { (acc[l.date] = acc[l.date] || []).push(l); return acc; }, {});
  const todayLessons = items.filter((l) => l.date === todayIso);
  const isDayOpen = (date) => (expanded[date] !== undefined ? expanded[date] : (date === todayIso || view === 'jour'));
  const toggleDay = (date) => setExpanded((prev) => ({ ...prev, [date]: !isDayOpen(date) }));

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 mt-8" data-testid="my-planning-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <SectionTitle kicker="Planning" title={isTeacher ? 'Ma journée de travail' : 'Mes prochaines leçons'} sub="Planning géré par le secrétariat — mise à jour automatique toutes les 30 s." />
          <div className="flex items-center gap-2 shrink-0 mt-1">
            {isTeacher && (
              <button onClick={() => { setAddOpen(true); setAddMsg(''); }} data-testid="planning-add-btn" className="h-9 px-4 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[12.5px] font-bold inline-flex items-center gap-1.5">
                + Leçon
              </button>
            )}
            <button onClick={() => load(true)} disabled={refreshing} data-testid="planning-refresh-btn" className="h-9 px-4 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-[12.5px] font-semibold inline-flex items-center gap-2">
              <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
        </div>
        {!loading && linked && isTeacher && (
          <div className="rounded-2xl border border-amber-600/30 bg-amber-950/10 px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="today-summary">
            <div>
              <div className="text-amber-400 text-[11px] font-bold uppercase tracking-[0.18em] font-mono-spec">Aujourd'hui · {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
              <div className="text-white text-[17px] font-semibold mt-0.5">
                {todayLessons.length === 0 ? 'Aucune leçon aujourd\'hui 🎉' : `${todayLessons.length} leçon${todayLessons.length > 1 ? 's' : ''} · ${Math.round(todayLessons.reduce((a, l) => a + l.duration_min, 0) / 60 * 10) / 10} h de travail`}
              </div>
            </div>
            {todayLessons.length > 0 && (
              <div className="text-white/60 text-[12.5px]">
                {todayLessons[0].start_time} → {todayLessons[todayLessons.length - 1].start_time} · 1ʳᵉ leçon : <b className="text-white/85">{todayLessons[0].student_name}</b>
              </div>
            )}
          </div>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
        ) : !linked ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="my-planning-unlinked">
            <CalendarDays size={26} className="mx-auto text-white/25 mb-3" />
            <div className="text-white/70 text-[14px] font-semibold">Pas encore de fiche au secrétariat</div>
            <p className="text-white/40 text-[12.5px] mt-2 max-w-md mx-auto">Le secrétariat doit créer {isTeacher ? 'ta fiche enseignant' : 'ta fiche élève'} avec ton adresse email ({user.email}) pour que ton planning apparaisse ici.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/45 text-[13px]" data-testid="my-planning-empty">Aucune leçon à venir pour le moment.</div>
        ) : (
          <>
            {/* Filtres de période : Jour / Semaine / Mois / Tout */}
            <div className="mb-4" data-testid="planning-view-bar">
              <div className="flex items-center gap-1.5 flex-wrap">
                {VIEWS.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => changeView(v.id)}
                    data-testid={`planning-view-${v.id}`}
                    className={`h-9 px-4 rounded-full text-[12.5px] font-bold transition ${view === v.id ? 'bg-amber-500 text-black shadow-md shadow-amber-900/30' : 'border border-white/15 text-white/65 hover:text-white hover:border-white/35'}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {view !== 'tout' && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-1.5 py-1.5">
                  <button onClick={() => moveOffset(-1)} data-testid="planning-prev" aria-label="Période précédente" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white/75 active:scale-95 inline-flex items-center justify-center shrink-0"><ChevronLeft size={17} /></button>
                  <div className="flex-1 text-center min-w-0">
                    <div className="text-white text-[13.5px] font-semibold capitalize truncate" data-testid="planning-period-label">{periodLabel(view, pStart, pEnd)}</div>
                    {offset !== 0 && (
                      <button onClick={() => { setOffset(0); setExpanded({}); }} data-testid="planning-back-today" className="text-amber-400 hover:text-amber-300 text-[11px] font-bold">↩ Revenir à aujourd&apos;hui</button>
                    )}
                  </div>
                  <button onClick={() => moveOffset(1)} data-testid="planning-next" aria-label="Période suivante" className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white/75 active:scale-95 inline-flex items-center justify-center shrink-0"><ChevronRight size={17} /></button>
                </div>
              )}
              <div className="mt-2 text-white/45 text-[12px]" data-testid="planning-period-count">{filtered.length} leçon{filtered.length > 1 ? 's' : ''} {view === 'tout' ? 'à venir' : 'sur la période'}</div>
            </div>

            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/45 text-[13px]" data-testid="planning-period-empty">Aucune leçon sur cette période.</div>
            ) : (
          <div className="space-y-3" data-testid="my-planning-list">
            {Object.entries(byDate).map(([date, lessons]) => {
              const isToday = date === todayIso;
              return (
              <div key={date} className={isToday ? 'rounded-2xl border border-amber-600/35 bg-amber-950/[0.06] p-3 -mx-3' : undefined}>
                <button onClick={() => toggleDay(date)} data-testid={`planning-day-${date}`} className="w-full flex items-center justify-between gap-2 py-1.5">
                  <span className={`text-[12px] font-bold uppercase tracking-wider text-left ${isToday ? 'text-amber-300' : 'text-amber-400/70'}`}>
                    {isToday ? '📍 Aujourd\'hui · ' : ''}{new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                  <span className="inline-flex items-center gap-2 text-white/50 text-[11.5px] font-mono-spec shrink-0">
                    {lessons.length} leçon{lessons.length > 1 ? 's' : ''}
                    <ChevronDown size={15} className={`transition-transform ${isDayOpen(date) ? 'rotate-180 text-amber-400' : ''}`} />
                  </span>
                </button>
                {isDayOpen(date) && (
                <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] divide-y divide-white/5 mt-1">
                  {lessons.map((l) => {
                    const badge = STATUS_BADGE[l.status];
                    return (
                    <div key={l.id} className="flex items-center gap-3 px-4 py-3.5 flex-wrap" data-testid={`planning-lesson-${l.id}`}>
                      <span className="w-1.5 h-9 rounded-full shrink-0" style={{ background: l.teacher_color }} />
                      <span className="font-mono-spec text-white text-[14px] font-bold w-16">{l.start_time}</span>
                      {isTeacher ? (
                        <button
                          onClick={() => { setActionLesson(l); setMoveOpen(false); setCancelOpen(false); setActMsg(''); }}
                          data-testid={`lesson-actions-${l.id}`}
                          className="flex-1 min-w-[160px] text-left group"
                          title="Ouvrir la fiche flash ou le bilan de cet élève"
                        >
                          <div className="text-white/90 group-hover:text-white text-[13.5px] font-semibold truncate inline-flex items-center gap-1">
                            {l.student_name} <ChevronRight size={13} className="text-white/35 group-hover:text-amber-400 shrink-0" />
                          </div>
                          <div className="text-white/45 text-[12px]">{<TypeTag type={l.type} />} · {l.duration_min} min{l.notes ? ` · ${l.notes}` : ''}</div>
                        </button>
                      ) : (
                        <div className="flex-1 min-w-[160px]">
                          <div className="text-white/90 text-[13.5px] font-semibold truncate">{`Avec ${l.teacher_name}`}</div>
                          <div className="text-white/45 text-[12px]">{<TypeTag type={l.type} />} · {l.duration_min} min{l.notes ? ` · ${l.notes}` : ''}</div>
                        </div>
                      )}
                      {isTeacher && l.student_phone && (
                        <a
                          href={`tel:${l.student_phone.replace(/\s/g, '')}`}
                          data-testid={`call-student-${l.id}`}
                          title={`Appeler ${l.student_name} · ${l.student_phone}`}
                          className="w-12 h-12 sm:w-9 sm:h-9 rounded-xl sm:rounded-full bg-sky-600/15 border border-sky-500/40 text-sky-300 hover:bg-sky-600/30 active:scale-[0.95] transition inline-flex items-center justify-center shrink-0"
                        >
                          <Phone size={16} />
                        </a>
                      )}
                      {badge && <span className={`px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${badge[1]}`}>{badge[0]}</span>}
                      {isTeacher && l.status === 'planifie' && (
                        <button
                          onClick={() => completeLesson(l)}
                          disabled={completing === l.id}
                          data-testid={`complete-lesson-${l.id}`}
                          className="w-full sm:w-auto h-12 sm:h-9 px-4 rounded-xl sm:rounded-full bg-emerald-600 hover:bg-emerald-500 active:scale-[0.98] transition disabled:opacity-50 text-white text-[13px] sm:text-[12px] font-bold inline-flex items-center justify-center gap-1.5"
                          title="Marque la leçon effectuée et ouvre le bilan pré-rempli"
                        >
                          {completing === l.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Leçon terminée
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
                )}
              </div>
              );
            })}
          </div>
            )}
          </>
        )}

        {/* Panneau d'actions au clic sur un élève du planning */}
        {actionLesson && (
          <div className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setActionLesson(null)} data-testid="lesson-action-sheet">
            <div className="w-full max-w-md bg-[#0f0f10] border border-white/10 rounded-2xl p-5 pb-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="text-amber-400 text-[10.5px] font-bold uppercase tracking-[0.18em] font-mono-spec">Fin de cours · que veux-tu laisser à l&apos;élève ?</div>
                  <div className="text-white text-[17px] font-semibold truncate mt-0.5">{actionLesson.student_name}</div>
                  <div className="text-white/45 text-[12px]">{new Date(actionLesson.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · {actionLesson.start_time} · {<TypeTag type={actionLesson.type} />}</div>
                </div>
                <button onClick={() => setActionLesson(null)} data-testid="lesson-action-close" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center shrink-0"><X size={16} /></button>
              </div>
              {flashHistory !== null && (() => {
                const k = normName(actionLesson.student_name);
                const prev = flashHistory.filter((f) => normName(f.student_name) === k);
                return (
                  <div className="mb-3 rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-[12px] leading-snug" data-testid="lesson-flash-history">
                    {prev.length > 0 ? (
                      <span className="text-emerald-200">🗂 <b>{prev.length} fiche{prev.length > 1 ? 's' : ''} de séance déjà enregistrée{prev.length > 1 ? 's' : ''}</b> pour cet élève — dernière le {new Date(prev[0].created_at).toLocaleDateString('fr-FR')}. Ta trace est conservée sur codevoiture.fr, même sans compte élève.</span>
                    ) : (
                      <span className="text-white/55">🗂 Aucune fiche encore pour cet élève — crée la première ci-dessous : elle sera conservée sur codevoiture.fr et intégrée à son parcours dès qu&apos;il créera son compte.</span>
                    )}
                  </div>
                );
              })()}
              {editingName === null ? (
                <button onClick={() => { setEditingName(actionLesson.student_name); setFixMsg(''); }} data-testid="lesson-fix-name-btn" className="mb-3 text-white/55 hover:text-amber-300 text-[12px] font-semibold inline-flex items-center gap-1.5">
                  <Pencil size={12} /> Corriger le nom / autre élève
                </button>
              ) : (
                <div className="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 space-y-2" data-testid="lesson-fix-form">
                  <input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    data-testid="lesson-fix-input"
                    placeholder="Prénom Nom"
                    className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500"
                  />
                  <button onClick={() => fixStudent('rename')} disabled={fixSaving} data-testid="lesson-fix-rename" className="w-full h-10 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[12.5px] font-bold">
                    {fixSaving ? '…' : "✏️ Corriger l'orthographe (renomme sa fiche partout)"}
                  </button>
                  <button onClick={() => fixStudent('reassign')} disabled={fixSaving} data-testid="lesson-fix-reassign" className="w-full h-10 rounded-lg border border-white/20 text-white/80 hover:bg-white/5 disabled:opacity-50 text-[12.5px] font-bold">
                    👤 C&apos;est un autre élève (réaffecte cette leçon)
                  </button>
                  <button onClick={() => setEditingName(null)} className="w-full text-white/45 hover:text-white/70 text-[11.5px]" data-testid="lesson-fix-cancel">Annuler</button>
                </div>
              )}
              {fixMsg && <div className="mb-3 text-emerald-300 text-[12px] font-semibold" data-testid="lesson-fix-msg">{fixMsg}</div>}
              <div className="space-y-2.5">
                <button
                  onClick={() => { setFinFor({ name: actionLesson.student_name, univers: actionLesson.type === 'moto_plateau' ? 'moto' : 'voiture' }); setActionLesson(null); }}
                  data-testid="lesson-action-fin-cours"
                  className="w-full flex items-center gap-3 rounded-xl border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 active:scale-[0.99] transition p-4 text-left"
                >
                  <span className="w-11 h-11 rounded-xl bg-amber-500/20 border border-amber-500/50 text-amber-300 flex items-center justify-center shrink-0"><CheckCircle2 size={18} /></span>
                  <span className="min-w-0">
                    <span className="block text-white text-[14px] font-bold">⭐ Bilan fin de cours guidé</span>
                    <span className="block text-white/55 text-[12px] leading-snug mt-0.5">Les + et les −, acquis (observé · compris · acquis), prochaine leçon proposée. Texte perso + version courte Codes Rousseau + QR avec ressenti élève.</span>
                  </span>
                </button>
                <button
                  onClick={() => { setFlashFor({ name: actionLesson.student_name, kind: 'bilan' }); setActionLesson(null); }}
                  data-testid="lesson-action-flash"
                  className="w-full flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 active:scale-[0.99] transition p-4 text-left"
                >
                  <span className="w-11 h-11 rounded-xl bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center shrink-0"><Sparkles size={18} /></span>
                  <span className="min-w-0">
                    <span className="block text-white text-[14px] font-bold">Fiche flash · cours du jour à cocher</span>
                    <span className="block text-white/55 text-[12px] leading-snug mt-0.5">QR à scanner : l&apos;élève voit le cours (ou crée son compte). S&apos;il a déjà un compte, tout s&apos;intègre automatiquement à son parcours.</span>
                  </span>
                </button>
                <button
                  onClick={() => { setFlashFor({ name: actionLesson.student_name, kind: 'prep' }); setActionLesson(null); }}
                  data-testid="lesson-action-prep"
                  className="w-full flex items-center gap-3 rounded-xl border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 active:scale-[0.99] transition p-4 text-left"
                >
                  <span className="w-11 h-11 rounded-xl bg-sky-600/20 border border-sky-500/40 text-sky-300 flex items-center justify-center shrink-0"><BookOpen size={18} /></span>
                  <span className="min-w-0">
                    <span className="block text-white text-[14px] font-bold">Préparer le prochain cours</span>
                    <span className="block text-white/55 text-[12px] leading-snug mt-0.5">Donne les thèmes à réviser et ce que tu attends de lui — partage par QR, SMS ou texte.</span>
                  </span>
                </button>
                <button
                  onClick={() => { const n = actionLesson.student_name; setActionLesson(null); navigate(`/enseignant/examen-blanc-grille?eleve=${encodeURIComponent(n)}`); }}
                  data-testid="lesson-action-bilan"
                  className="w-full flex items-center gap-3 rounded-xl border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 active:scale-[0.99] transition p-4 text-left"
                >
                  <span className="w-11 h-11 rounded-xl bg-red-600/20 border border-red-500/40 text-red-300 flex items-center justify-center shrink-0"><ClipboardCheck size={18} /></span>
                  <span className="min-w-0">
                    <span className="block text-white text-[14px] font-bold">Bilan de fin de leçon</span>
                    <span className="block text-white/55 text-[12px] leading-snug mt-0.5">Grille d&apos;évaluation pré-remplie au nom de {actionLesson.student_name}, envoyable dans sa messagerie.</span>
                  </span>
                </button>
                {/* Reporter / Annuler */}
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    onClick={() => { setMoveOpen((v) => !v); setCancelOpen(false); setActMsg(''); setMoveForm({ date: actionLesson.date, start_time: actionLesson.start_time }); }}
                    data-testid="lesson-action-move"
                    className="flex items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 transition p-3 text-amber-200 text-[13px] font-bold"
                  >
                    📆 Reporter
                  </button>
                  <button
                    onClick={() => { setCancelOpen((v) => !v); setMoveOpen(false); setActMsg(''); }}
                    data-testid="lesson-action-cancel"
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] transition p-3 text-white/75 text-[13px] font-bold"
                  >
                    ✖️ Annuler
                  </button>
                </div>
                {moveOpen && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-3 space-y-2" data-testid="lesson-move-form">
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" value={moveForm.date} onChange={(e) => setMoveForm((p) => ({ ...p, date: e.target.value }))} data-testid="lesson-move-date" className="h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                      <input type="time" value={moveForm.start_time} onChange={(e) => setMoveForm((p) => ({ ...p, start_time: e.target.value }))} data-testid="lesson-move-time" className="h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                    </div>
                    <button onClick={doReschedule} disabled={actSaving} data-testid="lesson-move-confirm" className="w-full h-10 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[12.5px] font-bold">
                      {actSaving ? '…' : 'Confirmer le report'}
                    </button>
                  </div>
                )}
                {cancelOpen && (
                  <div className="rounded-xl border border-white/15 bg-white/[0.03] p-3 space-y-1.5" data-testid="lesson-cancel-form">
                    <div className="text-white/55 text-[11.5px] font-mono-spec uppercase mb-1">Motif (registre DSR)</div>
                    <button onClick={() => doCancel('annule')} disabled={actSaving} data-testid="lesson-cancel-annule" className="w-full h-10 rounded-lg border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200 text-[12.5px] font-bold">Leçon annulée</button>
                    <button onClick={() => doCancel('absent_excuse')} disabled={actSaving} data-testid="lesson-cancel-excuse" className="w-full h-10 rounded-lg border border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 text-orange-200 text-[12.5px] font-bold">Absence excusée</button>
                    <button onClick={() => doCancel('absent_non_excuse')} disabled={actSaving} data-testid="lesson-cancel-nonexcuse" className="w-full h-10 rounded-lg border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200 text-[12.5px] font-bold">Absence non excusée</button>
                  </div>
                )}
                {actMsg && <div className={`text-[12.5px] font-semibold ${actMsg.includes('✓') ? 'text-emerald-300' : 'text-red-300'}`} data-testid="lesson-act-msg">{actMsg}</div>}
                {actionLesson.student_phone && (
                  <a href={`tel:${actionLesson.student_phone.replace(/\s/g, '')}`} data-testid="lesson-action-call" className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] transition p-3.5 text-left">
                    <span className="w-9 h-9 rounded-xl bg-sky-600/15 border border-sky-500/40 text-sky-300 flex items-center justify-center shrink-0"><Phone size={15} /></span>
                    <span className="text-white/80 text-[13px] font-semibold">Appeler · {actionLesson.student_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal ajout de leçon par l'enseignant */}
        {addOpen && (
          <div className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setAddOpen(false)} data-testid="planning-add-modal">
            <form onSubmit={submitAdd} className="w-full max-w-md bg-[#0f0f10] border border-white/10 rounded-2xl p-5 pb-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-amber-400 text-[10.5px] font-bold uppercase tracking-[0.18em] font-mono-spec">Mon planning</div>
                  <div className="text-white text-[18px] font-semibold">Ajouter une leçon</div>
                </div>
                <button type="button" onClick={() => setAddOpen(false)} data-testid="planning-add-close" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center shrink-0"><X size={16} /></button>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block">
                    <span className="text-white/55 text-[11px] font-mono-spec uppercase">Date</span>
                    <input type="date" required value={addForm.date} onChange={(e) => setAddForm((p) => ({ ...p, date: e.target.value }))} data-testid="add-lesson-date" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                  </label>
                  <label className="block">
                    <span className="text-white/55 text-[11px] font-mono-spec uppercase">Heure</span>
                    <input type="time" required value={addForm.start_time} onChange={(e) => setAddForm((p) => ({ ...p, start_time: e.target.value }))} data-testid="add-lesson-time" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block">
                    <span className="text-white/55 text-[11px] font-mono-spec uppercase">Durée</span>
                    <select value={addForm.duration_min} onChange={(e) => setAddForm((p) => ({ ...p, duration_min: e.target.value }))} data-testid="add-lesson-duration" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-2 text-white text-[13px] outline-none focus:border-amber-500">
                      {[30, 45, 60, 90, 120, 180].map((d) => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-white/55 text-[11px] font-mono-spec uppercase">Type</span>
                    <select value={addForm.type} onChange={(e) => setAddForm((p) => ({ ...p, type: e.target.value }))} data-testid="add-lesson-type" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-2 text-white text-[13px] outline-none focus:border-amber-500">
                      {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-white/55 text-[11px] font-mono-spec uppercase">Élève (fiche créée si inconnue)</span>
                  <input value={addForm.student_name} onChange={(e) => setAddForm((p) => ({ ...p, student_name: e.target.value }))} placeholder="Prénom Nom" data-testid="add-lesson-student" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                </label>
                <label className="block">
                  <span className="text-white/55 text-[11px] font-mono-spec uppercase">Note (optionnel)</span>
                  <input value={addForm.notes} onChange={(e) => setAddForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Ex. : plateau, rattrapage…" data-testid="add-lesson-notes" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px] outline-none focus:border-amber-500" />
                </label>
                {addMsg && <div className={`text-[12.5px] font-semibold ${addMsg.includes('✓') ? 'text-emerald-300' : 'text-red-300'}`} data-testid="add-lesson-msg">{addMsg}</div>}
                <button type="submit" disabled={addSaving} data-testid="add-lesson-submit" className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[13.5px] font-bold">
                  {addSaving ? 'Ajout…' : 'Ajouter à mon planning'}
                </button>
              </div>
            </form>
          </div>
        )}

        <FlashSeanceModal open={!!flashFor} kind={flashFor?.kind || 'bilan'} initialStudentName={flashFor?.name || ''} onClose={() => setFlashFor(null)} />
        <FinDeCoursModal open={!!finFor} initialStudentName={finFor?.name || ''} initialUnivers={finFor?.univers || 'voiture'} onClose={() => setFinFor(null)} />
      </section>
    </PageShell>
  );
}
